import clsx from "clsx";
import { HTMLAttributes, forwardRef } from "react";
import Control from "../control";
import PaginationItem, {
  Ellipsis,
  First,
  Last,
  Next,
  Prev,
} from "./paginationItem";

export interface PaginationProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "children"
> {
  pageIndex: number;
  pageCount: number;
  pageSize: number;
  canNextPage: boolean;
  canPreviousPage: boolean;
  size?: "sm" | "lg";
  fullNav?: boolean;
  hideTotalPage?: boolean;
  setPageSize: (size: number) => void;
  gotoPage: (page: number) => void;
  previousPage: () => void;
  nextPage: () => void;
}

const Pagination = forwardRef<HTMLDivElement, PaginationProps>((props, ref) => {
  const {
    className,
    pageIndex,
    pageCount,
    pageSize,
    canNextPage,
    canPreviousPage,
    size = "lg",
    fullNav = true,
    hideTotalPage = false,
    setPageSize,
    gotoPage,
    nextPage,
    previousPage,
    ...rest
  } = props;

  const controlSize = size === "lg" ? undefined : size;

  return (
    <div {...rest} ref={ref} className="flex flex-row gap-2">
      {!fullNav && (
        <span
          className={clsx("text-nowrap px-2", {
            "pt-2.5": size === "lg",
            "pt-1.25": size === "sm",
          })}
        >
          Page {pageIndex + 1} {hideTotalPage ? "" : `/ ${pageCount}`}
        </span>
      )}
      <Control.Select
        size={controlSize}
        options={[
          { id: "5", label: "5" },
          { id: "10", label: "10" },
          { id: "20", label: "20" },
          { id: "50", label: "50" },
        ]}
        value={pageSize.toString()}
        onChange={(value) => setPageSize(Number.parseInt(value!.toString()))}
      />
      <div
        className={clsx(
          "shrink-0 border border-gunmetal-100",
          "[&>*:not(:last-child)]:border-e [&>*:not(:last-child)]:border-e-gunmetal-100",
          "*:min-w-10",
        )}
      >
        <First
          onClick={() => gotoPage(0)}
          disabled={pageIndex === 0}
          size={controlSize}
        />
        <Prev
          onClick={previousPage}
          disabled={!canPreviousPage}
          size={controlSize}
        />
        {fullNav &&
          pageCount <= 10 &&
          Array.from(new Array(pageCount).keys()).map((page) => (
            <PaginationItem
              key={page}
              active={page === pageIndex}
              onClick={() => gotoPage(page)}
              size={controlSize}
            >
              {page + 1}
            </PaginationItem>
          ))}
        {fullNav && pageCount > 10 && (
          <>
            {pageIndex >= 4 && (
              <>
                <PaginationItem
                  active={pageIndex === 0}
                  onClick={() => gotoPage(0)}
                  size={controlSize}
                >
                  1
                </PaginationItem>
                <Ellipsis size={controlSize} />
              </>
            )}
            {Array.from(
              new Array(
                pageIndex >= 4 && pageIndex < pageCount - 5 ? 3 : 5,
              ).keys(),
            ).map((index) => {
              let start = 0;
              if (pageIndex >= 4 && pageIndex < pageCount - 5)
                start = Math.max(0, pageIndex - 1);
              else if (pageIndex >= pageCount - 5) start = pageCount - 5;

              return (
                <PaginationItem
                  key={index}
                  active={pageIndex === start + index}
                  onClick={() => gotoPage(start + index)}
                  size={controlSize}
                >
                  {start + index + 1}
                </PaginationItem>
              );
            })}
            {pageIndex < pageCount - 5 && (
              <>
                <Ellipsis size={controlSize} />
                <PaginationItem
                  active={pageIndex === pageCount - 1}
                  onClick={() => gotoPage(pageCount - 1)}
                  size={controlSize}
                >
                  {pageCount}
                </PaginationItem>
              </>
            )}
          </>
        )}
        <Next onClick={nextPage} disabled={!canNextPage} size={controlSize} />
        {fullNav && (
          <Last
            onClick={() => gotoPage(pageCount - 1)}
            disabled={pageIndex === pageCount - 1}
            size={controlSize}
          />
        )}
      </div>
    </div>
  );
});

export default Pagination;
